import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import Layout from "../components/Layout";
import SalesHistorySection from "../components/SalesHistorySection";
import useFinanceData from "../hooks/useFinanceData";
import { useRestaurant } from "../contexts/RestaurantContext";
import { format, isSameMonth, parseISO } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";
import { Badge } from "components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const FORECAST_RANGE_RATE = 0.12;

const CATEGORY_COLORS = {
  Wareneinkauf: "bg-blue-50 text-blue-700",
  Miete: "bg-purple-50 text-purple-700",
  Personal: "bg-green-50 text-green-700",
  "Strom/Gas": "bg-yellow-50 text-yellow-700",
  Versicherungen: "bg-orange-50 text-orange-700",
  Marketing: "bg-pink-50 text-pink-700",
  Reparaturen: "bg-red-50 text-red-700",
  Sonstiges: "bg-slate-100 text-slate-700",
};

export default function FinancePerformance() {
  const { t, i18n } = useTranslation();
  const finance = useFinanceData();
  const { currentRestaurantId } = useRestaurant();
  const locale = i18n.language === "de" ? de : enUS;

  // Day detail dialog
  const [selectedDay, setSelectedDay] = useState(null);

  const { data: forecastResponse, isLoading: forecastLoading } = useQuery({
    queryKey: ['forecast', 'revenue', currentRestaurantId ?? 'default', 7],
    queryFn: () => base44.forecast.getRevenue(7),
    placeholderData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000,
  });
  const forecastDays = useMemo(
    () => forecastResponse?.forecasts || [],
    [forecastResponse],
  );

  const handleChartClick = (data) => {
    if (data?.activePayload?.[0]) {
      setSelectedDay(data.activePayload[0].payload);
    }
  };

  const monthLabel = format(finance.selectedMonth, "MMMM yyyy", { locale });

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const formatChartDayLabel = useCallback((dateStr) => {
    const chartDate = parseISO(dateStr);
    return isSameMonth(chartDate, finance.selectedMonth)
      ? format(chartDate, "d")
      : format(chartDate, "d.M.");
  }, [finance.selectedMonth]);

  const forecastRows = useMemo(() => {
    const cogsRate = finance.historicalCogsRate;
    const laborRate = finance.historicalLaborRate;

    return forecastDays.map((forecastDay) => {
      const revenue = forecastDay.revenue || 0;
      const cogs = forecastDay.cogs ?? forecastDay.material_cost ?? (revenue * cogsRate);
      const laborCost = forecastDay.labor_cost ?? (revenue * laborRate);
      const otherExpenses = forecastDay.other_expenses ?? 0;
      const fixedCosts = forecastDay.fixed_costs ?? 0;
      const totalExpenses = forecastDay.total_expenses ?? (cogs + laborCost + otherExpenses + fixedCosts);
      const profit = forecastDay.profit ?? (revenue - totalExpenses);

      return {
        day: parseISO(forecastDay.date).getDate(),
        dayLabel: formatChartDayLabel(forecastDay.date),
        date: forecastDay.date,
        revenue,
        costs: totalExpenses,
        profit,
        cogs,
        laborCost,
        otherExpenses,
        fixedCosts,
        shifts: [],
        expenseDetails: forecastDay.expense_details || [],
        isFuture: forecastDay.date > todayStr,
        isForecast: true,
        isClosed: forecastDay.is_closed,
        revenueLow: revenue * (1 - FORECAST_RANGE_RATE),
        revenueHigh: revenue * (1 + FORECAST_RANGE_RATE),
        costsLow: totalExpenses * (1 - FORECAST_RANGE_RATE),
        costsHigh: totalExpenses * (1 + FORECAST_RANGE_RATE),
      };
    });
  }, [finance.historicalCogsRate, finance.historicalLaborRate, forecastDays, todayStr, formatChartDayLabel]);

  const chartData = useMemo(() => {
    const actualRows = finance.dailyData
      .filter((day) => !day.isFuture && (!finance.isCurrentMonth || day.date < todayStr))
      .map((day) => ({
        ...day,
        dayLabel: formatChartDayLabel(day.date),
        isForecast: false,
        isClosed: false,
        actualRevenue: day.revenue,
        actualCosts: day.costs,
        forecastRevenue: null,
        forecastCosts: null,
        forecastRevenueLow: null,
        forecastRevenueBand: null,
        forecastCostsLow: null,
        forecastCostsBand: null,
      }));

    if (!finance.isCurrentMonth) {
      return actualRows;
    }

    const actualDates = new Set(actualRows.map((row) => row.date));
    const futureForecastRows = forecastRows
      .filter((row) => row.date >= todayStr && !actualDates.has(row.date))
      .map((row) => ({
        ...row,
        actualRevenue: null,
        actualCosts: null,
        forecastRevenue: row.revenue,
        forecastCosts: row.costs,
        forecastRevenueLow: row.revenueLow,
        forecastRevenueBand: row.revenueHigh - row.revenueLow,
        forecastCostsLow: row.costsLow,
        forecastCostsBand: row.costsHigh - row.costsLow,
      }));

    return [...actualRows, ...futureForecastRows].sort((a, b) => a.date.localeCompare(b.date));
  }, [finance.dailyData, finance.isCurrentMonth, forecastRows, todayStr, formatChartDayLabel]);

  const renderChartTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const point = payload[0]?.payload;
    if (!point) return null;

    const revenueValue = point.actualRevenue ?? point.forecastRevenue;
    const costsValue = point.actualCosts ?? point.forecastCosts;

    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-medium text-slate-900">{label}</span>
          {point.isForecast && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
              {t("finances.forecastLabel", "Forecast")}
            </span>
          )}
        </div>
        <div className="space-y-1">
          {revenueValue !== null && revenueValue !== undefined && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{t("finances.revenue")}</span>
              <span className="font-medium text-slate-900">
                {revenueValue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </span>
            </div>
          )}
          {costsValue !== null && costsValue !== undefined && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{t("finances.costs")}</span>
              <span className="font-medium text-slate-900">
                {costsValue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </span>
            </div>
          )}
          {point.isForecast && (
            <p className="pt-1 text-xs text-slate-400">
              ±{Math.round(FORECAST_RANGE_RATE * 100)}% range
            </p>
          )}
        </div>
      </div>
    );
  }, [t]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-10">

        {/* ── Section 1: Month nav + 3 key numbers ── */}
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-5 sm:mb-8">
            <button
              onClick={finance.prevMonth}
              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-semibold text-slate-800 min-w-[7rem] sm:min-w-[10rem] text-center capitalize">
              {monthLabel}
            </span>
            <button
              onClick={finance.nextMonth}
              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            {finance.isCurrentMonth && (
              <span className="text-sm text-slate-400 w-full sm:w-auto sm:ml-2">
                {finance.openDaysPassed}/{finance.totalOpenDays} {t("revenue.openingDays")}
                {finance.guests > 0 && (
                  <> · {finance.guests} {t("dashboard.guests")}</>
                )}
              </span>
            )}
          </div>

          {(() => {
            const fmt = (n) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
            const showForecast = finance.isCurrentMonth;
            const momRow = (value, positiveIsGood = true) => {
              if (value === null) return null;
              const good = positiveIsGood ? value >= 0 : value <= 0;
              const sign = value >= 0 ? "+" : "";
              return (
                <span className={`text-xs ${good ? "text-green-600" : "text-red-500"}`}>
                  {sign}{value.toFixed(1)}% vs {t("revenue.previousMonth")}
                </span>
              );
            };

            const rows = [
              {
                key: "revenue",
                label: t("finances.revenue"),
                actual: fmt(finance.revenue),
                forecast: showForecast ? fmt(finance.projectedRevenue) : null,
                mom: finance.comparisonLastMonthRevenue > 0 ? momRow(finance.revenueMoM, true) : null,
              },
              {
                key: "costs",
                label: t("finances.costs"),
                actual: fmt(finance.totalExpenses),
                forecast: showForecast ? fmt(finance.projectedExpenses) : null,
                mom: finance.comparisonLastMonthTotalExpenses > 0 ? momRow(finance.expensesMoM, false) : null,
              },
              {
                key: "profit",
                label: t("finances.profit"),
                actual: fmt(finance.profit),
                actualClass: finance.profit >= 0 ? "text-green-600" : "text-red-600",
                actualMeta: `${finance.profitMargin.toFixed(1)}% ${t("finances.margin")}`,
                forecast: showForecast ? fmt(finance.projectedProfit) : null,
                forecastClass: finance.projectedProfit >= 0 ? "text-green-600" : "text-red-600",
                forecastMeta: showForecast ? `${finance.projectedProfitMargin.toFixed(1)}% ${t("finances.margin")}` : null,
                mom: finance.comparisonLastMonthProfit > 0 ? momRow(finance.profitMoM, true) : null,
              },
            ];

            return (
              <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 sm:grid-cols-[8rem_1fr_1fr] sm:gap-x-8 gap-y-5 items-baseline">
                <div />
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  {t("finances.actual")}
                </div>
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  {showForecast ? `${t("finances.forecast")} · ${t("finances.fullMonth")}` : ""}
                </div>

                {rows.map((row) => (
                  <React.Fragment key={row.key}>
                    <div className="text-sm text-slate-500">{row.label}</div>
                    <div>
                      <p className={`text-2xl sm:text-3xl font-semibold tabular-nums ${row.actualClass || "text-slate-900"}`}>
                        {row.actual}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                        {row.actualMeta && <span className="text-slate-400">{row.actualMeta}</span>}
                        {row.mom}
                      </div>
                    </div>
                    <div>
                      {row.forecast ? (
                        <>
                          <p className={`text-2xl sm:text-3xl font-semibold tabular-nums ${row.forecastClass || "text-slate-500"}`}>
                            {row.forecast}
                          </p>
                          {row.forecastMeta && (
                            <p className="mt-1 text-xs text-slate-400">{row.forecastMeta}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-300">—</p>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            );
          })()}
        </div>

        {/* ── Section 3: 7-day forecast ── */}
        <div className="border border-slate-200 rounded-lg bg-white p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-800">{t("finances.forecastNext7Days")}</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">{t("finances.forecastSubtitle")}</p>
          {forecastLoading ? (
            <p className="text-sm text-slate-400 py-4 text-center">Loading…</p>
          ) : forecastDays.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">{t("dashboard.noForecastData", "Not enough data for a forecast yet.")}</p>
          ) : (() => {
            const cogsRate = finance.historicalCogsRate;
            const laborRate = finance.historicalLaborRate;
            const rows = forecastDays.map(f => {
              const revenue = f.is_closed ? 0 : (f.revenue || 0);
              const material = revenue * cogsRate;
              const labor = revenue * laborRate;
              const profit = revenue - material - labor;
              return { ...f, revenue, material, labor, profit };
            });
            const totals = rows.reduce(
              (acc, r) => ({ revenue: acc.revenue + r.revenue, material: acc.material + r.material, labor: acc.labor + r.labor, profit: acc.profit + r.profit }),
              { revenue: 0, material: 0, labor: 0, profit: 0 }
            );
            const fmt = (n) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
            const dayShort = { Montag: "Mo", Dienstag: "Di", Mittwoch: "Mi", Donnerstag: "Do", Freitag: "Fr", Samstag: "Sa", Sonntag: "So" };
            return (
              <>
                {/* Mobile: stacked card list (one card per day) */}
                <div className="md:hidden space-y-2">
                  {rows.map((f, i) => {
                    const dayCosts = f.material + f.labor;
                    return (
                      <div key={i} className="rounded-lg border border-slate-100 bg-white p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-800">
                            {dayShort[f.day_name] || f.day_name?.slice(0, 2)}
                            <span className="ml-1.5 text-xs font-normal text-slate-400">{f.date ? f.date.slice(5).replace("-", ".") : ""}</span>
                          </span>
                          {f.is_closed && (
                            <span className="text-xs text-slate-300">{t("finances.closed")}</span>
                          )}
                        </div>
                        {!f.is_closed && (
                          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                            <dt className="text-slate-400">{t("finances.colRevenue")}</dt>
                            <dd className="text-right text-slate-800 font-medium tabular-nums">{fmt(f.revenue)}</dd>
                            <dt className="text-slate-400">{t("finances.costs")}</dt>
                            <dd className="text-right text-slate-700 tabular-nums">{fmt(dayCosts)}</dd>
                            <dt className="text-slate-400">{t("finances.colProfit")}</dt>
                            <dd className={`text-right font-semibold tabular-nums ${f.profit >= 0 ? "text-green-600" : "text-red-500"}`}>{fmt(f.profit)}</dd>
                          </dl>
                        )}
                      </div>
                    );
                  })}
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="mb-2 text-sm font-semibold text-slate-600">{t("finances.total")}</p>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                      <dt className="text-slate-400">{t("finances.colRevenue")}</dt>
                      <dd className="text-right text-slate-800 font-semibold tabular-nums">{fmt(totals.revenue)}</dd>
                      <dt className="text-slate-400">{t("finances.costs")}</dt>
                      <dd className="text-right text-slate-700 font-semibold tabular-nums">{fmt(totals.material + totals.labor)}</dd>
                      <dt className="text-slate-400">{t("finances.colProfit")}</dt>
                      <dd className={`text-right font-semibold tabular-nums ${totals.profit >= 0 ? "text-green-600" : "text-red-500"}`}>{fmt(totals.profit)}</dd>
                    </dl>
                  </div>
                </div>
                {/* Desktop: original table */}
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-wide">
                      <th className="text-left pb-3 font-medium">{t("finances.colDay")}</th>
                      <th className="text-right pb-3 font-medium">{t("finances.colRevenue")}</th>
                      <th className="text-right pb-3 font-medium">{t("finances.costs")}</th>
                      <th className="text-right pb-3 font-medium">{t("finances.colProfit")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((f, i) => {
                      const dayCosts = f.material + f.labor;
                      return (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-2.5 text-slate-700 font-medium">
                            {dayShort[f.day_name] || f.day_name?.slice(0, 2)}&nbsp;
                            <span className="text-slate-400 font-normal text-xs">{f.date ? f.date.slice(5).replace("-", ".") : ""}</span>
                          </td>
                          <td className="py-2.5 text-right text-slate-800 font-medium tabular-nums">{f.is_closed ? "–" : fmt(f.revenue)}</td>
                          <td className="py-2.5 text-right text-slate-700 tabular-nums">{f.is_closed ? "–" : fmt(dayCosts)}</td>
                          <td className={`py-2.5 text-right font-semibold tabular-nums ${f.profit >= 0 ? "text-green-600" : "text-red-500"}`}>
                            {f.is_closed ? "–" : fmt(f.profit)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50 text-sm font-semibold">
                      <td className="py-3 pl-1 text-slate-600">{t("finances.total")}</td>
                      <td className="py-3 text-right text-slate-800 tabular-nums">{fmt(totals.revenue)}</td>
                      <td className="py-3 text-right text-slate-700 tabular-nums">{fmt(totals.material + totals.labor)}</td>
                      <td className={`py-3 text-right tabular-nums ${totals.profit >= 0 ? "text-green-600" : "text-red-500"}`}>{fmt(totals.profit)}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  {t("finances.forecastRateNote", {
                    materialPercent: (cogsRate * 100).toFixed(0),
                    laborPercent: (laborRate * 100).toFixed(0),
                  })}
                </p>
              </>
            );
          })()}
        </div>

        {/* ── Section 4: Daily chart ── */}
        <div className="border border-slate-200 rounded-lg bg-white p-4 sm:p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-1">
            {t("finances.dailyProfitLoss")}
          </h2>
          <p className="text-xs text-slate-400 mb-4">{t("revenue.clickDayForDetails")}</p>

          {chartData.some((d) => d.revenue > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} onClick={handleChartClick} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="forecastRevBandGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.14} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="forecastCostBandGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="dayLabel" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  content={renderChartTooltip}
                />
                <Area
                  type="monotone"
                  dataKey="actualRevenue"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#revGrad)"
                  name={t("finances.revenue")}
                  dot={false}
                  activeDot={{ r: 4, cursor: "pointer" }}
                />
                <Area
                  type="monotone"
                  dataKey="actualCosts"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  fill="none"
                  strokeDasharray="4 3"
                  name={t("finances.costs")}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
                <Area
                  type="monotone"
                  dataKey="forecastRevenueLow"
                  stackId="forecastRevenueRange"
                  stroke="none"
                  fill="none"
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="forecastRevenueBand"
                  stackId="forecastRevenueRange"
                  stroke="none"
                  fill="url(#forecastRevBandGrad)"
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="forecastCostsLow"
                  stackId="forecastCostsRange"
                  stroke="none"
                  fill="none"
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="forecastCostsBand"
                  stackId="forecastCostsRange"
                  stroke="none"
                  fill="url(#forecastCostBandGrad)"
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="forecastRevenue"
                  stroke="#2563eb"
                  strokeWidth={2}
                  fill="none"
                  strokeDasharray="6 4"
                  name={`${t("finances.revenue")} (${t("finances.forecastLabel", "Forecast")})`}
                  dot={false}
                  activeDot={{ r: 4, cursor: "pointer" }}
                  connectNulls
                />
                <Area
                  type="monotone"
                  dataKey="forecastCosts"
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  fill="none"
                  strokeDasharray="6 4"
                  name={`${t("finances.costs")} (${t("finances.forecastLabel", "Forecast")})`}
                  dot={false}
                  activeDot={{ r: 3 }}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">
              {t("posPerformance.noData")}
            </div>
          )}
        </div>

        {/* ── Section 5: Monthly records ── */}
        <SalesHistorySection selectedMonth={finance.selectedMonth} />

        {/* ── Day detail dialog ── */}
        <Dialog open={!!selectedDay} onOpenChange={(open) => { if (!open) setSelectedDay(null); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            {selectedDay && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-base">
                    {format(new Date(selectedDay.date), "EEEE, d. MMMM yyyy", { locale })}
                  </DialogTitle>
                </DialogHeader>

                {selectedDay.isForecast && (
                  <p className="text-sm text-blue-600 border-b border-slate-100 pb-3">
                    {t("finances.forecastLabel", "Forecast")}
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 py-3 border-b border-slate-100">
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.revenue")}</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedDay.revenue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.costs")}</p>
                    <p className="text-lg font-bold text-slate-900">
                      {selectedDay.costs.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{t("finances.profit")}</p>
                    <p className={`text-lg font-bold ${selectedDay.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {selectedDay.profit.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                </div>

                {!selectedDay.isForecast && selectedDay.guests > 0 && (
                  <p className="text-sm text-slate-500">
                    {selectedDay.guests} {t("dashboard.guests")} &middot; {(selectedDay.revenue / selectedDay.guests).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} {t("revenue.avgPerGuest")}
                  </p>
                )}

                {/* Cost breakdown */}
                <div className="space-y-2 py-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.costOfGoods")} (COGS)</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.cogs.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.laborCosts")}</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.laborCost.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                  {selectedDay.otherExpenses > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t("revenue.otherExpenses")}</span>
                      <span className="font-medium text-slate-900">
                        {selectedDay.otherExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("revenue.fixedCostsProportional")}</span>
                    <span className="font-medium text-slate-900">
                      {selectedDay.fixedCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                </div>

                {/* Shifts */}
                {selectedDay.shifts.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">{t("revenue.laborCostsDay")}</p>
                    <div className="space-y-1.5">
                      {selectedDay.shifts.map((s, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            {s.employee}
                            <span className="text-slate-400 ml-1.5">
                              {s.shift} · {s.hours}h
                            </span>
                          </span>
                          <span className="text-slate-700">
                            {selectedDay.isForecast ? "—" : `€${s.cost}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expenses logged that day */}
                {selectedDay.expenseDetails?.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">{t("finances.expensesOverview")}</p>
                    <div className="space-y-1.5">
                      {selectedDay.expenseDetails.map((exp, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            {exp.description || exp.category}
                            <Badge variant="secondary" className={`text-xs font-normal ml-1.5 ${CATEGORY_COLORS[exp.category] || ""}`}>
                              {t(`finances.categories.${exp.category}`, exp.category)}
                            </Badge>
                          </span>
                          <span className="text-slate-700">
                            {exp.amount?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
